import { useMemo } from 'react';
import { DESIGN_TOKENS } from '../../constants/designTokens';
import { parseAmount } from '../finance/financeUtils';

const VISIT_TYPE_COLORS = DESIGN_TOKENS.chart.visitTypeColorByName;
const clampScore = (value) => Math.max(0, Math.min(100, Math.round(value)));

export default function useStatisticsDerivedData({
  attendanceLogs = [],
  finances = [],
  annualBudget = {},
  isAll = false,
  periodKey = '4w',
  selectedDepartment,
  students = [],
  teachers = [],
  visitLogs = []
}) {
  const periodStartDate = useMemo(() => {
    const now = new Date();
    const start = new Date(now);

    if (periodKey === '1y') {
      start.setFullYear(now.getFullYear() - 1);
    } else if (periodKey === '3m') {
      start.setMonth(now.getMonth() - 3);
    } else {
      start.setDate(now.getDate() - 28);
    }

    return start;
  }, [periodKey]);

  const previousPeriodStartDate = useMemo(() => {
    const now = new Date();
    const durationMs = now.getTime() - periodStartDate.getTime();
    return new Date(periodStartDate.getTime() - durationMs);
  }, [periodStartDate]);

  const attendancePointLimit = useMemo(() => {
    if (periodKey === '1y') return 10;
    if (periodKey === '3m') return 8;
    return 4;
  }, [periodKey]);

  // [FIX] 하위 호환성: department || grade 패턴 사용
  const filteredStudents = useMemo(() => {
    if (isAll) return students;
    return students.filter((s) => (s.department || s.grade) === selectedDepartment);
  }, [students, selectedDepartment, isAll]);

  // [FIX] 교사 필터링: department가 없거나 빈 경우도 표시되도록 개선
  const filteredTeachers = useMemo(() => {
    if (isAll) return teachers;

    // 부서 필터링: department가 없거나 빈 문자열이면 모든 부서에서 표시
    return teachers.filter((t) => {
      if (!t.department || t.department === '' || t.department === 'All') return true;
      return t.department === selectedDepartment;
    });
  }, [teachers, selectedDepartment, isAll]);

  const filteredFinances = useMemo(() => {
    if (isAll) return finances;
    return finances.filter((f) => {
      // 부서 정보가 없거나 'All', '공통'인 경우 공용 데이터로 간주하여 합산
      const dept = f.department || f.deptName || '';
      return !dept || dept === 'All' || dept === '공통' || dept === selectedDepartment;
    });
  }, [finances, selectedDepartment, isAll]);

  // [Analytics Calculations]
  const attendanceStats = useMemo(() => {
    const recentLogs = [...attendanceLogs]
      .filter((log) => !!log?.date && new Date(log.date) >= periodStartDate)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-attendancePointLimit);
    if (recentLogs.length === 0) return [];

    // For specific department, filter logs. For All, calculate global.
    const studentIds = new Set(filteredStudents.map((s) => s.id));

    return recentLogs.map((log) => {
      const presentCount = Object.entries(log).filter(([id, status]) => studentIds.has(id) && (status === 'present' || status === 'late')).length;
      const total = filteredStudents.length;
      return {
        date: log.date.slice(5),
        rate: total > 0 ? Math.round((presentCount / total) * 100) : 0,
        count: presentCount
      };
    });
  }, [attendanceLogs, attendancePointLimit, filteredStudents, periodStartDate]);

  const avgAttendance = useMemo(() => {
    if (attendanceStats.length === 0) return { current: 0, delta: 0 };
    const current = Math.round(attendanceStats.reduce((a, b) => a + b.rate, 0) / attendanceStats.length);
    // [COMPARE] Previous week
    const prev = attendanceStats.length > 1 ? attendanceStats[attendanceStats.length - 2].rate : Math.max(0, current - 5);
    return { current, delta: current - prev };
  }, [attendanceStats]);

  const visitationStats = useMemo(() => {
    const visitedStudentIds = new Set();
    const prevVisitedStudentIds = new Set();
    const studentIds = new Set(filteredStudents.map((s) => s.id));

    visitLogs.forEach((log) => {
      if (!studentIds.has(log.studentId) || !log?.date) return;
      const logDate = new Date(log.date);
      if (logDate >= periodStartDate) {
        visitedStudentIds.add(log.studentId);
      } else if (logDate >= previousPeriodStartDate && logDate < periodStartDate) {
        prevVisitedStudentIds.add(log.studentId);
      }
    });

    const rate = filteredStudents.length > 0 ? Math.round((visitedStudentIds.size / filteredStudents.length) * 100) : 0;
    const prevCount = prevVisitedStudentIds.size;
    return { count: visitedStudentIds.size, rate, delta: visitedStudentIds.size - prevCount };
  }, [visitLogs, filteredStudents, periodStartDate, previousPeriodStartDate]);

  const financeStats = useMemo(() => {
    // [MOD] Resolve Budget based on Selected Department (similar to FinanceView)
    let currentBudget = 0;
    if (typeof annualBudget === 'number') {
      currentBudget = annualBudget;
    } else if (annualBudget) {
      if (annualBudget[selectedDepartment]) {
        currentBudget = annualBudget[selectedDepartment];
      } else {
        const normalize = (str) => str ? str.replace(/\s+/g, '') : '';
        const targetKey = Object.keys(annualBudget).find(key => normalize(key) === normalize(selectedDepartment));
        if (targetKey) {
          currentBudget = annualBudget[targetKey];
        } else if (annualBudget['All']) {
          currentBudget = annualBudget['All'];
        }
      }
    }

    // [FIX] 전체 재정 잔액 계산 (월간이 아닌 누적 잔액 + 기본 예산)
    const totalIncome = filteredFinances
      .filter((f) => f.type === '수입')
      .reduce((acc, curr) => acc + parseAmount(curr.amount), 0);
    const totalExpense = filteredFinances
      .filter((f) => f.type === '지출')
      .reduce((acc, curr) => acc + parseAmount(curr.amount), 0);
    const totalBalance = currentBudget + totalIncome - totalExpense;

    // [COMPARE] 선택 기간 변화량 계산
    const periodFinances = filteredFinances.filter((f) => {
      if (!f?.date) return false;
      const d = new Date(f.date);
      return d >= periodStartDate;
    });
    const periodIncome = periodFinances.filter((f) => f.type === '수입').reduce((acc, curr) => acc + parseAmount(curr.amount), 0);
    const periodExpense = periodFinances.filter((f) => f.type === '지출').reduce((acc, curr) => acc + parseAmount(curr.amount), 0);
    const monthlyDelta = periodIncome - periodExpense;

    return { income: totalIncome, expense: totalExpense, balance: totalBalance, delta: monthlyDelta };
  }, [filteredFinances, periodStartDate, annualBudget, selectedDepartment]);

  const faithStats = useMemo(() => {
    const stats = { new: 0, core: 0, needCare: 0, total: filteredStudents.length };
    filteredStudents.forEach((s) => {
      if (s.faithState === '초신자') stats.new++;
      else if (['여호수아', '기존신자', '양육중'].includes(s.faithState)) stats.core++;
      else stats.needCare++;
    });
    return stats;
  }, [filteredStudents]);

  // [NEW] 심방 유형별 통계 - 끝판왕 차트용
  const visitTypeStats = useMemo(() => {
    const studentIds = new Set(filteredStudents.map((s) => s.id));
    const typeCounts = {};

    visitLogs
      .filter((log) => studentIds.has(log.studentId))
      .forEach((log) => {
        const type = log.type || '기타';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });

    return Object.entries(typeCounts)
      .map(([name, value]) => ({ name, value, color: VISIT_TYPE_COLORS[name] || VISIT_TYPE_COLORS['기타'] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [visitLogs, filteredStudents]);

  const longTermAbsentees = useMemo(() => {
    if (attendanceLogs.length < 4) return [];
    const recentLogs = [...attendanceLogs].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 4);
    return filteredStudents.filter((student) => {
      const hasAttendance = recentLogs.some((log) => {
        const status = log[student.id];
        return status === 'present' || status === 'late';
      });
      return !hasAttendance;
    });
  }, [filteredStudents, attendanceLogs]);

  const latestAttendanceRecord = useMemo(() => {
    if (!attendanceLogs.length) return null;
    return [...attendanceLogs]
      .filter((log) => !!log?.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;
  }, [attendanceLogs]);

  const recentUnrecordedCount = useMemo(() => {
    if (!latestAttendanceRecord) return filteredStudents.length;
    return filteredStudents.filter((student) => {
      const status = latestAttendanceRecord[student.id];
      return !status || status === 'none';
    }).length;
  }, [filteredStudents, latestAttendanceRecord]);

  const latestDataDate = useMemo(() => {
    const latestAttendanceDate = latestAttendanceRecord?.date || '';
    const latestVisitDate = visitLogs
      .filter((log) => !!log?.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date || '';
    const latestFinanceDate = filteredFinances
      .filter((entry) => !!entry?.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date || '';

    return [latestAttendanceDate, latestVisitDate, latestFinanceDate]
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0] || '';
  }, [filteredFinances, latestAttendanceRecord, visitLogs]);

  const capabilityRadar = useMemo(() => {
    const totalStudents = filteredStudents.length;
    const settlementRate = totalStudents > 0 ? (faithStats.core / totalStudents) * 100 : 0;
    const longTermAbsenceRate = totalStudents > 0 ? (longTermAbsentees.length / totalStudents) * 100 : 0;
    const careStabilityScore = 100 - longTermAbsenceRate;

    let financeHealthScore = 50;
    if (financeStats.income > 0) {
      const spendRatio = financeStats.expense / financeStats.income;
      // 0.6 이하 지출 비율은 우수, 1.0 초과면 감점
      financeHealthScore = 100 - ((spendRatio - 0.6) * 80);
    } else if (financeStats.expense > 0) {
      financeHealthScore = 20;
    }

    const scores = [
      { key: 'attendance', label: '출석 안정성', score: clampScore(avgAttendance.current) },
      { key: 'settlement', label: '정착률', score: clampScore(settlementRate) },
      { key: 'visitation', label: '심방 커버리지', score: clampScore(visitationStats.rate) },
      { key: 'care', label: '케어 안정도', score: clampScore(careStabilityScore) },
      { key: 'finance', label: '재정 건전성', score: clampScore(financeHealthScore) },
    ];

    const average = scores.length > 0
      ? clampScore(scores.reduce((sum, item) => sum + item.score, 0) / scores.length)
      : 0;

    return { scores, average };
  }, [
    avgAttendance.current,
    faithStats.core,
    filteredStudents.length,
    financeStats.expense,
    financeStats.income,
    longTermAbsentees.length,
    visitationStats.rate
  ]);
  const demographicsStats = useMemo(() => {
    const genderDist = { '남': 0, '여': 0, '미상': 0 };
    const gradeDist = {};

    filteredStudents.forEach(s => {
      if (s.gender === '형제' || s.gender === '남성' || s.gender === '남') genderDist['남']++;
      else if (s.gender === '자매' || s.gender === '여성' || s.gender === '여') genderDist['여']++;
      else genderDist['미상']++;

      const grade = s.grade || '미분류';
      gradeDist[grade] = (gradeDist[grade] || 0) + 1;
    });

    const topGrades = Object.entries(gradeDist)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    return {
      gender: Object.entries(genderDist).map(([name, value]) => ({ name, value })),
      grades: topGrades
    };
  }, [filteredStudents]);

  const financeFlowStats = useMemo(() => {
    const monthlyData = {};
    filteredFinances.forEach(f => {
      if (!f.date) return;
      const monthLabel = f.date.substring(0, 7); // YYYY-MM
      if (!monthlyData[monthLabel]) monthlyData[monthLabel] = { month: monthLabel, 수입: 0, 지출: 0 };

      const amt = parseAmount(f.amount);
      if (f.type === '수입') monthlyData[monthLabel].수입 += amt;
      else if (f.type === '지출') monthlyData[monthLabel].지출 += amt;
    });

    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month)).slice(-6);
  }, [filteredFinances]);

  const teacherEngagementStats = useMemo(() => {
    const teacherLogs = {};
    filteredTeachers.forEach(t => teacherLogs[t.id || t.name] = { name: t.name, count: 0 });

    visitLogs.forEach(log => {
      if (!log.date) return;
      const logDate = new Date(log.date);
      if (logDate >= periodStartDate) {
        const tId = log.writerId || log.teacherId || log.teacherName;
        if (tId && teacherLogs[tId]) {
          teacherLogs[tId].count++;
        }
      }
    });

    const activeTeachers = Object.values(teacherLogs)
      .filter(t => t.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const uncounseledCount = Math.max(0, filteredStudents.length - visitationStats.count);

    return { activeTeachers, uncounseledCount };
  }, [visitLogs, filteredTeachers, filteredStudents.length, periodStartDate, visitationStats.count]);

  const newcomerFunnelStats = useMemo(() => {
    const stats = {
      등록: faithStats.new + faithStats.needCare + faithStats.core,
      진행: faithStats.needCare + faithStats.core,
      정착: faithStats.core
    };
    return [
      { step: '전체 인원', count: stats.등록 },
      { step: '양육/관리중', count: stats.진행 },
      { step: '정착 완료', count: stats.정착 },
    ];
  }, [faithStats]);

  // [NEW] Visitation Heatmap Stats (Github Contribution Style)
  const visitationHeatmapStats = useMemo(() => {
    // Generate last 12 weeks of data
    const weeks = 12;
    const now = new Date();
    // Set to most recent Saturday
    const end = new Date(now);
    end.setDate(now.getDate() - now.getDay() + 6); // End of current week (Saturday)
    end.setHours(23, 59, 59, 999);

    const start = new Date(end);
    start.setDate(end.getDate() - (weeks * 7) + 1); // Start of the 12 weeks (Sunday)
    start.setHours(0, 0, 0, 0);

    const heatmapData = [];
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

    // Initialize blank grid: 7 days x 12 weeks
    for (let day = 0; day < 7; day++) {
      for (let week = 0; week < weeks; week++) {
        const date = new Date(start);
        date.setDate(start.getDate() + (week * 7) + day);

        heatmapData.push({
          dayIndex: day,
          dayName: dayNames[day],
          weekIndex: week,
          date: date.toISOString().split('T')[0],
          count: 0
        });
      }
    }

    const studentIds = new Set(filteredStudents.map((s) => s.id));

    // Populate counts
    visitLogs.forEach((log) => {
      if (!studentIds.has(log.studentId) || !log?.date) return;
      const logDateStr = log.date.substring(0, 10);
      const logDate = new Date(logDateStr);

      if (logDate >= start && logDate <= end) {
        // Find corresponding cell
        const dayDiff = Math.floor((logDate.getTime() - start.getTime()) / (1000 * 3600 * 24));
        const cellWeek = Math.floor(dayDiff / 7);
        const cellDay = logDate.getDay();

        const cellIndex = heatmapData.findIndex(cell => cell.weekIndex === cellWeek && cell.dayIndex === cellDay);
        if (cellIndex !== -1) {
          heatmapData[cellIndex].count += 1;
        }
      }
    });

    return heatmapData;
  }, [visitLogs, filteredStudents]);

  // [NEW] Attendance Breakdown: present / late / absent per week (for stacked bar chart)
  const attendanceBreakdownStats = useMemo(() => {
    const recentLogs = [...attendanceLogs]
      .filter((log) => !!log?.date && new Date(log.date) >= periodStartDate)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-attendancePointLimit);
    if (recentLogs.length === 0) return [];

    const studentIds = new Set(filteredStudents.map((s) => s.id));
    const total = filteredStudents.length;

    return recentLogs.map((log) => {
      let present = 0, late = 0, absent = 0;
      studentIds.forEach(id => {
        const status = log[id];
        if (status === 'present') present++;
        else if (status === 'late') late++;
        else absent++;
      });
      return {
        date: log.date.slice(5),
        출석: total > 0 ? Math.round((present / total) * 100) : 0,
        지각: total > 0 ? Math.round((late / total) * 100) : 0,
        결석: total > 0 ? Math.round((absent / total) * 100) : 0,
        presentCount: present,
        lateCount: late,
        absentCount: absent,
      };
    });
  }, [attendanceLogs, attendancePointLimit, filteredStudents, periodStartDate]);

  // [NEW] Grade Treemap data
  const gradeTreemapStats = useMemo(() => {
    const gradeMap = {};
    filteredStudents.forEach(s => {
      const grade = s.department || s.grade || '미분류';
      gradeMap[grade] = (gradeMap[grade] || 0) + 1;
    });
    return Object.entries(gradeMap)
      .map(([name, size]) => ({ name, size }))
      .sort((a, b) => b.size - a.size)
      .slice(0, 8);
  }, [filteredStudents]);

  // [NEW] Attendance sparkline for KPI
  const attendanceSparkline = useMemo(() => {
    return attendanceStats.map(d => ({ v: d.rate }));
  }, [attendanceStats]);

  const thisWeekAttendance = attendanceStats.length > 0 ? attendanceStats[attendanceStats.length - 1] : { rate: 0, count: 0 };

  const gradeDetailedStats = useMemo(() => {
    const counts = { '중1': 0, '중2': 0, '중3': 0, '고1': 0, '고2': 0, '고3': 0 };
    filteredStudents.forEach(s => {
      const g = s.grade?.trim();
      if (counts[g] !== undefined) counts[g]++;
    });
    return counts;
  }, [filteredStudents]);

  return {
    attendanceBreakdownStats,
    attendanceSparkline,
    attendanceStats,
    avgAttendance,
    capabilityRadar,
    demographicsStats,
    faithStats,
    filteredStudents,
    filteredTeachers,
    financeFlowStats,
    financeStats,
    gradeDetailedStats,
    gradeTreemapStats,
    latestDataDate,
    longTermAbsentees,
    newcomerFunnelStats,
    recentUnrecordedCount,
    teacherEngagementStats,
    thisWeekAttendance,
    visitationHeatmapStats,
    visitationStats,
    visitTypeStats
  };
} 
